import { useCallback } from "react";
import {
  buildMarkdownSourceMap,
  markdownTableHeaderKey,
  tableColumnWidthsAt,
  type TableColumnWidthsList,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import { markdownBlockPreviousKinds } from "../../../lib/markdown-text";
import { TEXT_DEFAULT_COLOR } from "../../../lib/text-color";
import {
  resolveTableBodyPalette,
  type MarkdownTableBodyPalette,
} from "../../../lib/text-link-color";
import {
  renderSearchHighlightedText,
  StyledLinkifiedText,
  useSearchHighlightContext,
} from "./linkified-text";
import { MarkdownCalloutView } from "./markdown-callout-view";
import { MarkdownCodeBlockView } from "./markdown-code-block-view";
import { MarkdownLineView, type MarkdownInlineRenderer } from "./markdown-line-view";
import { MarkdownTableView } from "./markdown-table-view";

/**
 * 列幅の確定 1 回分。`tableCount` は「この本文から解析した表の総数」で、サーバがこれを超える
 * 保存値を切り詰める。本文を解析済みの本コンポーネントが数えて渡すことで、呼び出し側
 * （テキスト図形・付箋）が同じ本文をもう一度解析しなくて済む。
 */
export interface MarkdownTableColumnsChange {
  /** 本文中の表のブロック順 index。 */
  readonly tableIndex: number;
  /** 本文中の表の総数。 */
  readonly tableCount: number;
  /** 新しい列幅（em）。空配列は自動列幅へ戻すことを表す。 */
  readonly widthsEm: readonly number[];
  /** 解析した表のヘッダ指紋。空配列のときは空文字列。 */
  readonly headerKey: string;
}

interface Props {
  readonly text: string;
  /** 図形上で実際に描画する本文色。表本文のコントラスト補正にだけ使う。 */
  readonly bodyColor?: string | undefined;
  /** 図形の背景と本文色から解決した表本文の配色。 */
  readonly tableBodyPalette?: MarkdownTableBodyPalette | undefined;
  /**
   * 文字範囲だけへ適用する部分書式（正規形の run 配列）。
   * 未設定・空のときは従来どおり全文書式だけで描画する（見た目は変わらない）。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * 表の使用可能幅（em）。「（本文領域の幅 − 表の外枠線 2px）÷ 基準フォント px」を呼び出し側が
   * `markdownTableAvailableEm` で求めて渡す（DOM の実測に依らない。既定値は与えない）。
   * Canvas 出力も同じ式・同じ純粋関数で列幅を決めるため、画面と出力の列幅が一致する。
   */
  readonly availableEm: number;
  /**
   * 本文中の表（ブロック順）ごとの列幅（保存値）。未指定・`null` の表は自動列幅。
   * 列数とヘッダ指紋が一致するときだけ採用されるかは、共有関数が判定する。
   */
  readonly tableColumnWidths?: TableColumnWidthsList | undefined;
  /**
   * 列幅の確定。未指定のときはつまみを描かない（表示のみ）。
   * 参照専用・ロック中・編集中・コメント本文では未指定にする。
   * `widthsEm` が空配列のときは「保存を破棄して自動列幅へ戻す」を表す。
   */
  readonly onResizeTableColumns?: ((change: MarkdownTableColumnsChange) => void) | undefined;
  /** 図形の回転角（度）。つまみのドラッグ量を図形ローカル横軸へ射影するために必要。 */
  readonly rotationDeg?: number | undefined;
  /**
   * 本文の基準フォントサイズ（px）。つまみのドラッグ量を em へ換算するために使う。
   * 未指定のときはつまみを描かない（`availableEm` だけを渡す表示専用の利用を許すため）。
   */
  readonly baseFontPx?: number | undefined;
  /** 非編集・非ロックのキャンバスオブジェクトだけが渡す task 切り替えハンドラ。 */
  readonly onToggleTask?: ((checkMarkOffset: number) => void) | undefined;
}

// 付箋・テキストオブジェクトの非編集時に、本文（生の Markdown テキスト）を整形表示する。
// - 行単位のブロック記法のみ対応: 見出し（# 〜 ######）・箇条書き（- / * / +、ネスト対応）・
//   引用（>）・水平線（---）・番号付きリスト（1.、箇条書きと同じネスト）
// - 表（GFM 基本形）は domain のブロック層（sourceMap.blocks）で認識し、MarkdownTableView で描く。
//   セルの中身は行と同じ StyledLinkifiedText を渡すため、セル内のリンク・装飾も同じ挙動になる
// - 各行の本文は StyledLinkifiedText を通して描画し、見出し・箇条書きの中でも URL の
//   自動リンク化（クリックで新規タブ）が従来どおり機能するようにする。run の有無に依らず
//   同じ経路にするのは、インライン装飾（**太字** 等）が run を持たない本文にも現れるため
// - 行・リンク・run 境界・装飾境界の和集合で分割し、各区間へ文字色・文字背景・絶対 px サイズ・
//   太字の上書きと、記法の装飾（太字・斜体・取消線・インラインコード）を当てる
//   （分割判定は domain の共有関数が行う）
// - スタイル・表示テキストの決定は markdown-text.ts に集約しており、付箋フォント自動調整の
//   計測ビルダー（buildMarkdownTextContent）と同じ行構造・テキスト内容になる
//   （構造一致はパリティテストで担保。リンクの <a>/<span> はインライン要素で行の
//   フォント計測へ影響しない）
// - 引用の左罫・文字色と水平線の横罫は CSS クラス（markdownLineClassName）だけで与える。
//   水平線は表示テキストを持たず、空行と同じ `<br>` だけの構造にする（横罫は CSS の背景）
// - すべての行の div に `data-md-line`（raw 行 index）を付け、リモートキャレット層が行要素を
//   子要素の index ではなく属性で引けるようにする（表の行はセル要素だけがこの属性を持つ）
// - 記法を含まない本文は全行 plain の div となり、従来の見た目（pre-wrap のプレーン表示 +
//   リンク化）と一致する
export function MarkdownTextView({
  text,
  bodyColor = TEXT_DEFAULT_COLOR,
  tableBodyPalette = resolveTableBodyPalette({ bodyColor, fill: undefined }),
  inlineStyles,
  availableEm,
  tableColumnWidths,
  onResizeTableColumns,
  rotationDeg,
  baseFontPx,
  onToggleTask,
}: Props): JSX.Element {
  const sourceMap = buildMarkdownSourceMap(text);
  // 保存済み列幅は本文中の表のブロック順で並ぶため、ブロック index から「表だけを数えた index」を
  // 引けるようにしておく（描画中に可変変数を進めると、並びの前提が読み取りづらくなるため）。
  // 表の総数は、余剰の保存値をサーバ側で掃除できるよう確定時に渡す。
  const tableIndexByBlock = new Map<number, number>();
  for (const [blockIndex, block] of sourceMap.blocks.entries()) {
    if (block.kind === "table") tableIndexByBlock.set(blockIndex, tableIndexByBlock.size);
  }
  const tableCount = tableIndexByBlock.size;
  // 連続する箇条書きの項目間隔は、計測ビルダーと同じ共有関数で決める（表の直後は付かない）。
  const previousKinds = markdownBlockPreviousKinds(sourceMap);
  // 行・表のセル・callout のタイトルは、いずれもキャンバスオブジェクト用のリンク描画で描く。
  const renderInline: MarkdownInlineRenderer = (props) => <StyledLinkifiedText {...props} />;
  // コードブロックは Markdown を解釈しない別経路のため、検索語ハイライトだけをここで重ねる
  // （本文の一部である以上、フェンスの中でも一致文字を示す）。リンクにはならないので解除可能にする。
  const search = useSearchHighlightContext();
  const renderCodeText = useCallback(
    (codeText: string) => renderSearchHighlightedText(codeText, search, { dismissible: true }),
    [search]
  );
  return (
    <>
      {sourceMap.blocks.map((block, blockIndex) => {
        if (block.kind === "table") {
          const currentTableIndex = tableIndexByBlock.get(blockIndex) ?? 0;
          const onResize = onResizeTableColumns;
          return (
            <MarkdownTableView
              key={`table-${block.table.headerLineIndex}`}
              availableEm={availableEm}
              baseFontPx={baseFontPx}
              bodyColor={bodyColor}
              bodyPalette={tableBodyPalette}
              headerKey={markdownTableHeaderKey(text, block.table)}
              inlineStyles={inlineStyles}
              onResizeColumns={
                onResize === undefined
                  ? undefined
                  : (widthsEm, headerKey) =>
                      onResize({
                        headerKey,
                        tableCount,
                        tableIndex: currentTableIndex,
                        widthsEm,
                      })
              }
              renderCell={(spans, context) => (
                <StyledLinkifiedText
                  spans={spans}
                  tableBodyContext={context.kind === "body" ? context : undefined}
                />
              )}
              rotationDeg={rotationDeg}
              savedWidths={tableColumnWidthsAt(tableColumnWidths, currentTableIndex)}
              table={block.table}
              tableIndex={currentTableIndex}
              text={text}
            />
          );
        }
        if (block.kind === "code") {
          return (
            <MarkdownCodeBlockView
              key={`code-${block.code.fenceStartLineIndex}`}
              code={block.code}
              inlineStyles={inlineStyles}
              renderText={renderCodeText}
              sourceMap={sourceMap}
            />
          );
        }
        if (block.kind === "callout") {
          return (
            <MarkdownCalloutView
              key={`callout-${block.callout.openLineIndex}`}
              callout={block.callout}
              inlineStyles={inlineStyles}
              onToggleTask={onToggleTask}
              renderInline={renderInline}
              sourceMap={sourceMap}
            />
          );
        }
        return (
          // 行は本文位置と 1:1 対応の静的リストで並べ替えが起きないため行 index の key で問題ない。
          <MarkdownLineView
            key={block.lineIndex}
            inlineStyles={inlineStyles}
            lineIndex={block.lineIndex}
            previousKind={previousKinds[blockIndex]}
            renderInline={renderInline}
            sourceMap={sourceMap}
            onToggleTask={onToggleTask}
          />
        );
      })}
    </>
  );
}
